import React, { useState, useEffect } from 'react';
import { Plus, Trash2, ArrowUp, ArrowDown, Save, AlertCircle } from 'lucide-react';

const LMSQuestionBuilder = ({ initialSchema, onSave, mode }) => {
  const [schema, setSchema] = useState({ module_feedback: [], platform_feedback: [] });
  const [activeTab, setActiveTab] = useState(mode === 'platform' ? 'platform_feedback' : 'module_feedback');

  useEffect(() => {
    if (initialSchema && Object.keys(initialSchema).length > 0) {
      setSchema({
        module_feedback: initialSchema.module_feedback || [],
        platform_feedback: initialSchema.platform_feedback || []
      });
    }
  }, [initialSchema]);

  const handleSave = () => {
    onSave(schema);
  };

  const currentQuestions = schema[activeTab] || [];

  const updateQuestion = (index, field, value) => {
    const updated = [...currentQuestions];
    updated[index] = { ...updated[index], [field]: value };
    setSchema({ ...schema, [activeTab]: updated });
  };

  const updateCondition = (index, field, value) => {
    const updated = [...currentQuestions];
    if (!updated[index].condition) {
      updated[index].condition = { dependsOn: '', equals: '' };
    }
    updated[index].condition = { ...updated[index].condition, [field]: value };
    
    // If both fields are empty, remove the condition entirely to keep schema clean
    if (!updated[index].condition.dependsOn && !updated[index].condition.equals) {
      delete updated[index].condition;
    }
    
    setSchema({ ...schema, [activeTab]: updated });
  };

  const addOption = (index) => {
    const updated = [...currentQuestions];
    if (!updated[index].options) updated[index].options = [];
    updated[index].options.push(`Option ${updated[index].options.length + 1}`);
    setSchema({ ...schema, [activeTab]: updated });
  };

  const updateOption = (qIndex, oIndex, value) => {
    const updated = [...currentQuestions];
    updated[qIndex].options[oIndex] = value;
    setSchema({ ...schema, [activeTab]: updated });
  };

  const removeOption = (qIndex, oIndex) => {
    const updated = [...currentQuestions];
    updated[qIndex].options.splice(oIndex, 1);
    setSchema({ ...schema, [activeTab]: updated });
  };

  const moveQuestion = (index, direction) => {
    if (direction === -1 && index === 0) return;
    if (direction === 1 && index === currentQuestions.length - 1) return;
    
    const updated = [...currentQuestions];
    const temp = updated[index];
    updated[index] = updated[index + direction];
    updated[index + direction] = temp;
    setSchema({ ...schema, [activeTab]: updated });
  };

  const deleteQuestion = (index) => {
    const updated = [...currentQuestions];
    updated.splice(index, 1);
    setSchema({ ...schema, [activeTab]: updated });
  };

  const addQuestion = () => {
    const newQ = {
      id: `q_${Date.now().toString().slice(-6)}`,
      type: 'text',
      text: 'New Question'
    };
    setSchema({ ...schema, [activeTab]: [...currentQuestions, newQ] });
  };

  const renderQuestionCard = (q, index) => (
    <div key={index} style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', padding: '20px', marginBottom: '20px', position: 'relative', boxShadow: '0 4px 10px rgba(0,0,0,0.03)' }}>
      {/* Header Actions */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={() => moveQuestion(index, -1)} disabled={index === 0} style={{ padding: '6px', cursor: index === 0 ? 'not-allowed' : 'pointer', background: 'var(--bg-main)', border: '1px solid var(--border)', borderRadius: '6px', color: 'var(--text-muted)' }}><ArrowUp size={16} /></button>
          <button onClick={() => moveQuestion(index, 1)} disabled={index === currentQuestions.length - 1} style={{ padding: '6px', cursor: index === currentQuestions.length - 1 ? 'not-allowed' : 'pointer', background: 'var(--bg-main)', border: '1px solid var(--border)', borderRadius: '6px', color: 'var(--text-muted)' }}><ArrowDown size={16} /></button>
        </div>
        <button onClick={() => deleteQuestion(index)} style={{ padding: '6px', cursor: 'pointer', background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: '6px', color: '#ef4444' }}><Trash2 size={16} /></button>
      </div>

      {/* Main Fields */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '16px', marginBottom: '16px' }}>
        <div>
          <label style={{ display: 'block', fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: '4px', fontWeight: 600 }}>Question ID (Unique)</label>
          <input 
            type="text" 
            value={q.id || ''} 
            onChange={e => updateQuestion(index, 'id', e.target.value)}
            style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg-main)', color: 'var(--text-main)', boxSizing: 'border-box' }}
          />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: '4px', fontWeight: 600 }}>Question Type</label>
          <select 
            value={q.type || 'text'} 
            onChange={e => updateQuestion(index, 'type', e.target.value)}
            style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg-main)', color: 'var(--text-main)', boxSizing: 'border-box' }}
          >
            <option value="text">Open Text</option>
            <option value="rating">1-5 Rating</option>
            <option value="choice">Multiple Choice</option>
          </select>
        </div>
      </div>

      <div style={{ marginBottom: '16px' }}>
        <label style={{ display: 'block', fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: '4px', fontWeight: 600 }}>Question Text</label>
        <input 
          type="text" 
          value={q.text || ''} 
          onChange={e => updateQuestion(index, 'text', e.target.value)}
          style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg-main)', color: 'var(--text-main)', boxSizing: 'border-box' }}
        />
      </div>

      {/* Options for Choice Type */}
      {q.type === 'choice' && (
        <div style={{ marginBottom: '16px', padding: '16px', background: 'var(--bg-main)', borderRadius: '8px', border: '1px solid var(--border)' }}>
          <label style={{ display: 'block', fontSize: '0.85rem', color: 'var(--text-main)', marginBottom: '12px', fontWeight: 600 }}>Choices</label>
          {q.options?.map((opt, oIndex) => (
            <div key={oIndex} style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
              <input 
                type="text" 
                value={opt} 
                onChange={e => updateOption(index, oIndex, e.target.value)}
                style={{ flex: 1, padding: '8px', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-main)', boxSizing: 'border-box' }}
              />
              <button onClick={() => removeOption(index, oIndex)} style={{ padding: '8px', background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer' }}><Trash2 size={16} /></button>
            </div>
          ))}
          <button onClick={() => addOption(index)} style={{ marginTop: '8px', padding: '8px 12px', background: 'var(--bg-card)', border: '1px dashed var(--border)', borderRadius: '6px', color: 'var(--primary)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.85rem', fontWeight: 600 }}><Plus size={14} /> Add Option</button>
        </div>
      )}

      {/* Conditions */}
      <div style={{ padding: '16px', background: 'rgba(245, 158, 11, 0.05)', borderRadius: '8px', border: '1px solid rgba(245, 158, 11, 0.2)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
          <AlertCircle size={16} color="#f59e0b" />
          <label style={{ fontSize: '0.85rem', color: 'var(--text-main)', fontWeight: 600, margin: 0 }}>Visibility Condition (Optional)</label>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <div>
            <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '4px' }}>Show only if Question ID:</label>
            <select
              value={q.condition?.dependsOn || ''} 
              onChange={e => updateCondition(index, 'dependsOn', e.target.value)}
              style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-main)', fontSize: '0.85rem', boxSizing: 'border-box' }}
            >
              <option value="">-- Select a Previous Question --</option>
              {currentQuestions.slice(0, index).map((prevQ) => (
                <option key={prevQ.id} value={prevQ.id}>
                  {prevQ.text ? prevQ.text.substring(0, 30) + (prevQ.text.length > 30 ? '...' : '') : prevQ.id} ({prevQ.id})
                </option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '4px' }}>Equals Value:</label>
            <input 
              type="text" 
              placeholder="e.g. Yes"
              value={q.condition?.equals || ''} 
              onChange={e => updateCondition(index, 'equals', e.target.value)}
              style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-main)', fontSize: '0.85rem', boxSizing: 'border-box' }}
            />
          </div>
        </div>
      </div>

    </div>
  );

  return (
    <div style={{ background: 'var(--bg-main)', borderRadius: '12px', padding: '24px', border: '1px solid var(--border)' }}>
      
      {/* Header & Tabs */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        {!mode && (
          <div className="accounts-tabs" style={{ margin: 0 }}>
            <button 
              className={activeTab === 'module_feedback' ? 'admin-pill-tab active' : 'admin-pill-tab'}
              onClick={() => setActiveTab('module_feedback')}
            >
              Module Feedback
            </button>
            <button 
              className={activeTab === 'platform_feedback' ? 'admin-pill-tab active' : 'admin-pill-tab'}
              onClick={() => setActiveTab('platform_feedback')}
            >
              Platform Feedback
            </button>
          </div>
        )}
        {mode === 'course' && <h4 style={{ margin: 0, color: 'var(--text-main)', fontWeight: 600 }}>Course Feedback Questions</h4>}
        {mode === 'platform' && <h4 style={{ margin: 0, color: 'var(--text-main)', fontWeight: 600 }}>Platform Feedback Questions</h4>}
        <button 
          onClick={handleSave}
          style={{ padding: '10px 24px', background: 'var(--primary)', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', boxShadow: '0 4px 12px rgba(14, 165, 233, 0.3)' }}
        >
          <Save size={18} /> Save Schema
        </button>
      </div>

      {/* Questions List */}
      <div>
        {currentQuestions.map((q, i) => renderQuestionCard(q, i))}
        
        <button 
          onClick={addQuestion}
          style={{ width: '100%', padding: '16px', background: 'transparent', border: '2px dashed var(--primary)', borderRadius: '12px', color: 'var(--primary)', fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', cursor: 'pointer', transition: 'all 0.2s' }}
        >
          <Plus size={20} /> Add New Question
        </button>
      </div>

    </div>
  );
};

export default LMSQuestionBuilder;
